<template>
  <div class="row mt-3">
    <div class="col-sm-12">
      <div class="card">
        <div class="card-header">
          <h6 class="mb-0">{{ helper.trans("labels.plan_settings") }}</h6>
          <small class="text-muted">{{ helper.trans("labels.plan_settings_description") }}</small>
        </div>
        
        <div class="card-body">
          <LoaderWrapper v-if="loading" :zoom="0.5">
            <div class="text-center">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          </LoaderWrapper>

          <div v-else-if="planSettings && planSettings.length > 0">
            <div class="mb-3">
              <div class="form-check">
                <input 
                  id="plan-none"
                  :checked="!form.planId || form.planId === null"
                  @change="togglePlan(null)"
                  class="form-check-input" 
                  type="radio"
                  name="planSelection"
                >
                <label for="plan-none" class="form-check-label">
                  {{ helper.trans('labels.none') }}
                </label>
              </div>
            </div>
            <div v-for="setting in planSettings" :key="setting.id" class="mb-3">
              <div class="form-check">
                <input 
                  :id="`plan-${setting.id}`"
                  :checked="form.planId === setting.id"
                  @change="togglePlan(setting.id)"
                  class="form-check-input" 
                  type="radio"
                  name="planSelection"
                >
                <label :for="`plan-${setting.id}`" class="form-check-label d-flex flex-column">
                  <span>
                    <span class="fw-bold">{{ setting.name }}</span>
                    <span class="text-muted ms-2">({{ helper.trans('labels.currency') }}: {{ setting.currency }})</span>
                    <span class="badge bg-secondary badge-sm ms-2">{{ helper.trans('plan_settings.plan_type_' + (setting.planType || 'mandatory')) }}</span>
                  </span>
                  <span v-if="getDealAmountRangeText(setting)" class="text-muted small mt-1">{{ getDealAmountRangeText(setting) }}</span>
                </label>
              </div>
            </div>
          </div>

          <div v-else class="text-center text-muted">
            <small>{{ helper.trans('plan_settings.no_settings_available') }}</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, ref, computed, onMounted} from 'vue'
import {helper} from "@/service/CommonHelper"
import APIClient from "@/service/APIClient"
import LoaderWrapper from "@/components/LoaderWrapper.vue"
import TraderRequisitesSettingDTO from "@/models/TraderRequisitesSettingDTO"
import { PlanType } from "@/models/enum/PlanType"
import { PlanRewardType } from "@/models/enum/PlanRewardType"
import {useNotification} from "@kyvg/vue3-notification"
import {useStore} from "vuex"

export default defineComponent({
  name: 'TraderPlanSettingsBlock',
  components: {
    LoaderWrapper
  },
  props: {
    trader: {
      type: Object,
      required: true
    }
  },
  emits: ['plan-updated'],
  setup(props, { emit }) {
    const api = new APIClient()
    const notification = useNotification()
    const store = useStore()
    
    const loading = ref(false)
    const planSettings = ref<TraderRequisitesSettingDTO[]>([])
    
    const form = computed(() => props.trader)
    
    const isAdmin = computed(() => {
      return store.getters.isAdmin
    })

    const loadPlanSettings = async () => {
      try {
        loading.value = true
        const response = await api.traderRequisitesSettings()
        planSettings.value = (response || []).map(s => ({
          ...s,
          planType: s.planType ?? PlanType.MANDATORY,
          rewardType: s.rewardType ?? PlanRewardType.UNLOCK_REQUISITES,
          rewardPriority: s.rewardPriority ?? null
        }))
      } catch (error) {
        notification.notify({
          type: 'error',
          text: helper.trans('server_errors.INTERNAL_ERROR')
        })
      } finally {
        loading.value = false
      }
    }

    const togglePlan = (planId: number | null) => {
      props.trader.planId = planId

      emit('plan-updated', {
        traderId: props.trader.id,
        planId: planId
      })
    }

    const getDealAmountRangeText = (setting: TraderRequisitesSettingDTO): string | null => {
      const min = setting.planDealAmountMin?.trim() || null
      const max = setting.planDealAmountMax?.trim() || null
      if (min && max) return helper.trans('plan_settings.plan_deal_amount_range', { min, max })
      if (min) return helper.trans('plan_settings.plan_deal_amount_min') + ': ' + min
      if (max) return helper.trans('plan_settings.plan_deal_amount_max') + ': ' + max
      return null
    }

    onMounted(() => {
      if (planSettings.value.length === 0) {
        loadPlanSettings()
      }
    })

    return {
      loading,
      planSettings,
      isAdmin,
      togglePlan,
      getDealAmountRangeText,
      helper,
      form
    }
  }
})
</script>
